import {
  createUseStorageItem,
  getTimeRangeFromDurationString,
  isDurationString,
  isRelativeTimeValue,
  type TimeRangeInputValue,
} from '@chronosphereio/core';
import { parseISO } from 'date-fns';
import { useCallback, useSyncExternalStore } from 'react';
import { DEFAULT_TIME_RANGE_INPUT_VALUE } from '@/core';
import { pinnedStatusManager } from './pinned-time-range-singletons';

/**
 * Returns the currently pinned `TimeRangeValue` (which could be from another tab), as well as a method for setting it.
 */
export function usePinnedTimeRange() {
  const [pinnedTimeRange, setPinnedTimeRange] = usePinnedTimeRangeValue();
  return { pinnedTimeRange, setPinnedTimeRange };
}

/**
 * Returns the current `PinnedStatus` for this tab, as well as methods for manipulating it.
 */
export function usePinnedTimeRangeStatus() {
  const pinnedStatus = useSyncExternalStore(
    pinnedStatusManager.subscribeToPinnedStatus,
    pinnedStatusManager.getPinnedStatus
  );

  const { setPinnedTimeRange } = usePinnedTimeRange();

  const pin = useCallback(
    (value?: Parameters<typeof setPinnedTimeRange>[0]) => {
      if (value !== undefined) {
        setPinnedTimeRange(value);
      }
      pinnedStatusManager.pin();
    },
    [setPinnedTimeRange]
  );

  return {
    pinnedStatus,
    pin: pin,
    unpin: pinnedStatusManager.unpin,
    refresh: pinnedStatusManager.refresh,
  };
}

// Type representing dates being strings when parsed from storage
type ParsedTimeRange = { pastDuration: string; end?: string } | { start: string; end: string };

function parseTimeRange(serialized: string): TimeRangeInputValue {
  const parsed: ParsedTimeRange = JSON.parse(serialized);

  // Handle old RelativeTimeRange format
  if ('pastDuration' in parsed) {
    if (isDurationString(parsed.pastDuration) === false) {
      throw new Error('pastDuration is not a valid duration string');
    }

    return getTimeRangeFromDurationString(parsed.pastDuration);
  }

  return {
    start: parseDateOrRelativeTimeValue(parsed.start),
    end: parseDateOrRelativeTimeValue(parsed.end),
  };
}

function parseDateOrRelativeTimeValue(value: string): Date | string {
  if (isRelativeTimeValue(value)) {
    return value;
  }

  const date = parseISO(value);
  if (isNaN(date.getTime())) {
    throw new Error('String value was not a valid ISO date or relative time value');
  }

  return date;
}

// Really just exported for tests
export const PINNED_TIME_RANGE_STORAGE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE_V2';

const usePinnedTimeRangeValue = createUseStorageItem(PINNED_TIME_RANGE_STORAGE_KEY, DEFAULT_TIME_RANGE_INPUT_VALUE, {
  parse: parseTimeRange,
});
